import { useCallback, useEffect, useRef, useState } from 'react';

const HISTORY_KEY = 'pymego';

function serializeModule(module) {
  return module == null ? null : JSON.stringify(module);
}

function deserializeModule(serialized) {
  if (serialized == null) return null;
  try {
    return JSON.parse(serialized);
  } catch {
    return null;
  }
}

function historyState(module) {
  return { [HISTORY_KEY]: true, module: serializeModule(module) };
}

/**
 * Sincroniza el estado del módulo con history.pushState y maneja popstate
 * para que el botón Atrás del navegador navegue dentro de la SPA en lugar
 * de salir de la aplicación.
 */
export function useBrowserBackButton(initialModule = null) {
  const [module, setModuleState] = useState(initialModule);
  const skipPushRef = useRef(false);

  useEffect(() => {
    history.replaceState(historyState(null), '');

    const onPopState = (event) => {
      skipPushRef.current = true;
      if (event.state?.[HISTORY_KEY]) {
        setModuleState(deserializeModule(event.state.module));
      } else {
        history.pushState(historyState(null), '');
        setModuleState(null);
      }
      skipPushRef.current = false;
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const setModule = useCallback((next) => {
    setModuleState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      if (!skipPushRef.current) {
        history.pushState(historyState(value), '');
      }
      return value;
    });
  }, []);

  return [module, setModule];
}

/** Evita salir de la app con Atrás cuando no hay navegación interna (p. ej. login). */
export function useBlockBrowserExit(enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const state = historyState(null);
    history.replaceState(state, '');

    const onPopState = (event) => {
      if (!event.state?.[HISTORY_KEY]) {
        history.pushState(state, '');
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [enabled]);
}
